/**
 * Settings: the things about the app itself rather than about the food.
 *
 * Everything here was previously reachable only by editing DynamoDB by hand or by asking an
 * operator, which made a handful of ordinary decisions — when to be interrupted, what password
 * to use — somebody else's job.
 *
 * Household settings are saved the moment they are changed rather than behind a Save button.
 * The rule about not making the user think applies to this screen too: a control that has
 * visibly moved and a change that has actually been saved should be the same thing.
 */

import { useEffect, useLayoutEffect, useState } from 'preact/hooks';

import type { ApiClient } from '../api/client';
import type { PreferenceChange, Preferences } from '../api/types';
import { CognitoError, type Cognito } from '../auth/cognito';
import { asText, collect, type Reading } from '../pwa/diagnostics';
import { useReminders } from './reminders';

/** What the digest window may be set to, matching what the API will accept. */
const WINDOW_CHOICES = [1, 2, 3, 5, 7, 10, 14];

const HOURS_IN_DAY = 24;

interface Props {
  client: ApiClient;
  cognito: Cognito;
  onSignedOut: () => void;
}

export function Settings({ client, cognito, onSignedOut }: Props) {
  return (
    <div class="settings">
      <RemindersSection client={client} />
      <DigestSection client={client} />
      <PasswordSection cognito={cognito} />
      <AccountSection cognito={cognito} onSignedOut={onSignedOut} />
      <DiagnosticsSection />
    </div>
  );
}

/**
 * What this phone reports about itself.
 *
 * Here to settle where the add button actually lands, which has resisted being worked out from
 * a desktop. Delete it once that is understood; it is a debugging aid and not a feature.
 */
function DiagnosticsSection() {
  const [readings, setReadings] = useState<Reading[]>([]);
  const [copied, setCopied] = useState(false);

  const measure = (): void => {
    setReadings(collect());
    setCopied(false);
  };

  // Before paint rather than after it: these are measured from the DOM, so they cannot be had
  // at render, and measuring afterwards drew the list empty and then grew it a frame later.
  useLayoutEffect(measure, []);

  const copy = async (): Promise<void> => {
    await navigator.clipboard.writeText(asText(readings));
    setCopied(true);
  };

  return (
    <section>
      <h2>Diagnostics</h2>

      <dl class="readings">
        {readings.map((reading) => (
          <div key={reading.label}>
            <dt>{reading.label}</dt>
            <dd>{reading.value}</dd>
          </div>
        ))}
      </dl>

      <div class="setting">
        <button type="button" onClick={measure}>
          Measure again
        </button>
        <button type="button" onClick={() => void copy()}>
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </section>
  );
}

function RemindersSection({ client }: { client: ApiClient }) {
  const reminders = useReminders(client);

  return (
    <section>
      <h2>Reminders</h2>

      {reminders.state === null ? null : reminders.state === 'unsupported' ? (
        <p class="note">
          This browser cannot show reminders. On an iPhone they arrive once the app is on the
          Home Screen.
        </p>
      ) : reminders.state === 'blocked' ? (
        <p class="note">
          Reminders are blocked in your browser settings, which is the one thing this app cannot
          undo for you. Allow notifications for this site and come back.
        </p>
      ) : (
        <div class="setting">
          <div>
            <strong>One message a day</strong>
            <p class="note">
              {reminders.state === 'on'
                ? 'This device is told when something needs using.'
                : 'Nothing on this device will tell you when food is going off.'}
            </p>
          </div>
          <button
            type="button"
            disabled={reminders.working}
            onClick={reminders.state === 'on' ? reminders.turnOff : reminders.turnOn}
          >
            {reminders.state === 'on' ? 'Turn off' : 'Turn on'}
          </button>
        </div>
      )}
    </section>
  );
}

function DigestSection({ client }: { client: ApiClient }) {
  const [preferences, setPreferences] = useState<Preferences | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void client
      .preferences()
      .then(setPreferences)
      .catch(() => setProblem('could not load your settings'));
  }, [client]);

  const save = (change: PreferenceChange): void => {
    setSaving(true);
    setProblem(null);

    void client
      .changePreferences(change)
      .then(setPreferences)
      .catch(() => setProblem('could not save that — check your connection and try again'))
      .finally(() => setSaving(false));
  };

  return (
    <section>
      <h2>Daily digest</h2>
      <p class="note">
        One message a day about what needs using, rather than a ping per item. A muted app is a
        dead app.
      </p>

      <Choice
        label="Send it at"
        value={preferences === null ? null : String(preferences.digest_hour)}
        options={hourChoices()}
        describe={(hour) => describeHour(Number(hour))}
        saving={saving}
        onPick={(hour) => save({ digest_hour: Number(hour) })}
      />

      <Choice
        label="Mention food going off within"
        value={preferences === null ? null : String(preferences.digest_window_days)}
        options={windowChoices(preferences?.digest_window_days).map(String)}
        describe={(days) => describeWindow(Number(days))}
        saving={saving}
        onPick={(days) => save({ digest_window_days: Number(days) })}
      />

      <Choice
        label="Your timezone"
        value={preferences?.timezone ?? null}
        options={timezoneChoices(preferences?.timezone)}
        describe={(zone) => zone}
        saving={saving}
        onPick={(zone) => save({ timezone: zone })}
      />

      {problem === null ? null : (
        <p class="refusal" role="alert">
          {problem}
        </p>
      )}
    </section>
  );
}

/**
 * One stored choice, drawn at its full size before the stored value has arrived.
 *
 * A `null` value means the settings are still on their way. The field is still drawn, empty and
 * unusable, because the alternative — a line of "Loading…" that becomes three controls — moves
 * everything below it down the moment the network answers, and this screen's other sections need
 * nothing from the network at all. They would be readable, and then they would jump.
 */
function Choice({
  label,
  value,
  options,
  describe,
  saving,
  onPick,
}: {
  label: string;
  value: string | null;
  options: string[];
  describe: (option: string) => string;
  saving: boolean;
  onPick: (option: string) => void;
}) {
  return (
    <label>
      {label}
      <select
        value={value ?? ''}
        disabled={value === null || saving}
        onChange={(event) => onPick((event.target as HTMLSelectElement).value)}
      >
        {value === null
          ? null
          : options.map((option) => (
              <option key={option} value={option}>
                {describe(option)}
              </option>
            ))}
      </select>
    </label>
  );
}

function PasswordSection({ cognito }: { cognito: Cognito }) {
  const [current, setCurrent] = useState('');
  const [replacement, setReplacement] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [changed, setChanged] = useState(false);
  const [working, setWorking] = useState(false);

  const submit = async (): Promise<void> => {
    setProblem(null);
    setChanged(false);

    if (replacement !== confirmation) {
      setProblem('the new passwords do not match');
      return;
    }

    setWorking(true);
    try {
      await cognito.changePassword(current, replacement);
      setChanged(true);
      setCurrent('');
      setReplacement('');
      setConfirmation('');
    } catch (error) {
      setProblem(explainRefusal(error));
    } finally {
      setWorking(false);
    }
  };

  return (
    <section>
      <h2>Password</h2>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <label>
          Current password
          <input
            type="password"
            value={current}
            autocomplete="current-password"
            required
            onInput={(event) => setCurrent((event.target as HTMLInputElement).value)}
          />
        </label>

        <label>
          New password
          <input
            type="password"
            value={replacement}
            autocomplete="new-password"
            required
            onInput={(event) => setReplacement((event.target as HTMLInputElement).value)}
          />
        </label>

        <label>
          New password again
          <input
            type="password"
            value={confirmation}
            autocomplete="new-password"
            required
            onInput={(event) => setConfirmation((event.target as HTMLInputElement).value)}
          />
        </label>

        {problem === null ? null : (
          <p class="refusal" role="alert">
            {problem}
          </p>
        )}

        {changed ? (
          <p class="note" role="status">
            Password changed. You stay signed in on this device.
          </p>
        ) : null}

        <button type="submit" class="primary" disabled={working}>
          {working ? 'Changing…' : 'Change password'}
        </button>
      </form>
    </section>
  );
}

function AccountSection({
  cognito,
  onSignedOut,
}: {
  cognito: Cognito;
  onSignedOut: () => void;
}) {
  const identity = cognito.identity();

  return (
    <section>
      <h2>Account</h2>

      {identity === null ? null : <p class="note">Signed in as {identity.email}.</p>}

      <button
        type="button"
        onClick={() => {
          cognito.signOut();
          onSignedOut();
        }}
      >
        Sign out
      </button>

      <p class="note">
        Anything not yet synced stays on this device and goes up when you sign back in.
      </p>
    </section>
  );
}

/** Cognito's wording for a wrong password assumes you are signing in; here you are not. */
function explainRefusal(error: unknown): string {
  if (!(error instanceof CognitoError)) {
    return 'could not reach the sign-in service';
  }

  return error.kind === 'NotAuthorizedException'
    ? 'your current password is not right'
    : error.message;
}

/** `6 am` reads at a glance where `06:00` has to be decoded, and this is a glanced-at screen. */
export function describeHour(hour: number): string {
  const midday = 12;

  if (hour === 0) {
    return 'midnight';
  }
  if (hour === midday) {
    return 'midday';
  }

  return hour < midday ? `${hour} am` : `${hour - midday} pm`;
}

export function describeWindow(days: number): string {
  return days === 1 ? 'a day' : `${days} days`;
}

/** Every hour of the day, as a select deals in strings. */
function hourChoices(): string[] {
  return Array.from({ length: HOURS_IN_DAY }, (_, hour) => String(hour));
}

/** The offered windows, plus whatever is already stored, so a stored value is never lost. */
function windowChoices(current?: number): number[] {
  return current === undefined || WINDOW_CHOICES.includes(current)
    ? WINDOW_CHOICES
    : [...WINDOW_CHOICES, current].sort((one, other) => one - other);
}

/**
 * Every timezone the browser knows, with the stored one and this device's own guaranteed to be
 * in it — a household that moved should find its new zone at the top of the list rather than
 * having to know that "America/Denver" is what it is called.
 */
function timezoneChoices(current?: string): string[] {
  const known =
    typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [];
  const here = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return [...new Set([current ?? '', here, ...known])].filter((zone) => zone !== '');
}
